import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Copy } from 'lucide-react';
import { useCouponUsage } from '../hooks';
import { formatPrice } from '@/src/infrastructure/lib/currency';
import { TableSkeleton } from '@/src/modules/components/ui/LoadingSkeleton';
import { EmptyState, ErrorState } from '@/src/modules/components/ui/EmptyState';
import { formatDateTime } from '../lib/discount';

const th = 'px-3 py-2 text-[10px] uppercase tracking-widest font-bold text-on-surface-variant';

/** Backend exposes only purchaseId/userId/discount/date per redemption — no order number or email. */
export function CouponUsageTable({ couponId }: { couponId: string }) {
  const { data, isLoading, isError, error, refetch, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useCouponUsage(couponId);

  if (isLoading) return <TableSkeleton rows={3} cols={4} />;
  if (isError) return <ErrorState message={(error as Error).message} onRetry={() => void refetch()} />;
  const rows = data?.pages.flatMap((p) => p.items) ?? [];
  if (rows.length === 0) return <EmptyState title="Not redeemed yet" />;

  return (
    <div className="glass-panel rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-surface-container-low">
            <tr>
              <th className={th}>Date</th>
              <th className={th}>User</th>
              <th className={th}>Purchase</th>
              <th className={th}>Discount</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.purchaseId} className="border-t border-border-subtle">
                <td className="px-3 py-2 text-xs">{formatDateTime(u.createdAt)}</td>
                <td className="px-3 py-2 text-xs">
                  <Link to={`/users/${u.userId}`} className="font-mono hover:text-electric-violet">
                    {u.userId}
                  </Link>
                </td>
                <td className="px-3 py-2 text-xs font-mono">
                  <span className="select-all">{u.purchaseId}</span>
                  <CopyButton value={u.purchaseId} />
                </td>
                <td className="px-3 py-2 text-sm">{formatPrice(u.discountPaise)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {hasNextPage && (
        <div className="flex justify-center px-4 py-3 border-t border-border-subtle">
          <button
            onClick={() => void fetchNextPage()}
            disabled={isFetchingNextPage}
            className="px-3 py-1.5 rounded-md border border-border-subtle text-xs disabled:opacity-40 hover:bg-white/5"
          >
            {isFetchingNextPage ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () =>
    void navigator.clipboard?.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  return (
    <button
      type="button"
      onClick={copy}
      aria-label={copied ? 'Copied' : 'Copy purchase ID'}
      className="ml-2 inline-flex p-1.5 align-middle rounded text-on-surface-variant hover:text-on-surface hover:bg-white/5"
    >
      {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}
